import classNames from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import { useRef } from 'react';

import { css } from '@sorare/css';

import crest from '#src/assets/user/crest.webp';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';

import type { FileWithDataURL } from './useUploadFile.ts';
import { useUploadFile } from './useUploadFile.ts';

const error = css`
  color: var(--c-red-400);
`;
const container = css`
  position: relative;
  &.row {
    flex-direction: row;
  }
`;
const inputClass = css`
  position: absolute;
  opacity: 0;
  inset: 0;
  cursor: pointer;
`;
const image = css`
  &.sm {
    --image-size: calc(6 * var(--unit));
  }
  &.md {
    --image-size: calc(8 * var(--unit));
  }
  &.xl {
    --image-size: calc(12 * var(--unit));
  }

  width: var(--image-size);
  height: var(--image-size);
  object-fit: cover;
  border-radius: 50%;
`;

const onUploadClick = (e: MouseEvent<HTMLInputElement>) => {
  e.currentTarget.value = '';
};

type Props = {
  onChange: (file: FileWithDataURL) => void;
  currentFileUrl: string | null;
  name: string;
  validExtensions?: string[];
  buttonLabel?: ReactNode;
  children?: ReactNode;
  type: string;
  maxFileSizeMb?: number;
  row?: boolean;
  size?: 'sm' | 'md' | 'xl';
};
export const UploadFile = ({
  onChange,
  currentFileUrl,
  name,
  type,
  validExtensions,
  buttonLabel,
  children,
  maxFileSizeMb,
  row,
  size = 'xl',
}: Props) => {
  const { onDropFile, fileTypeError, fileSizeError, fileUrl, displayableFile } =
    useUploadFile(onChange, validExtensions, maxFileSizeMb);

  const input = useRef<HTMLInputElement>(null);

  const triggerFileUpload = () => {
    input.current!.click();
  };

  return (
    <Vertical gap={0}>
      {!!fileTypeError && <div className={error}>{fileTypeError}</div>}
      {!!fileSizeError && <div className={error}>{fileSizeError}</div>}
      <Vertical
        gap={2}
        align="center"
        className={classNames(container, { row })}
        data-sentry-block
      >
        <input
          className={inputClass}
          type="file"
          ref={input}
          name={name}
          onChange={e => {
            onDropFile(e);
          }}
          onClick={onUploadClick}
          accept={type}
        />
        {!children && !!displayableFile && (
          <img
            src={fileUrl || currentFileUrl || crest}
            alt={name}
            className={classNames(image, size)}
          />
        )}
        {children}
        {!!buttonLabel && (
          <Button color="quaternary" size="small" onClick={triggerFileUpload}>
            {buttonLabel}
          </Button>
        )}
      </Vertical>
    </Vertical>
  );
};
